import type {
  BreakdownDimension,
  PreviousUnavailable,
  RevenueBasis,
  RevenueFrame,
  RevenueNotAvailable,
  RevenueNullReason,
  SummaryMetricKey,
} from '@/api/revenue'
import { formatPercent } from '@/design'
import { formatDateKey } from '@/lib/filters'

/*
 * How the page words what the API says: each null's reason, why a comparison is missing,
 * and each figure's definition, quoted from docs/architecture/revenue-definitions.md §3 so
 * a tooltip says exactly what the API counts.
 */

/** Why a figure has nothing to divide by (`null_reason`). */
export const NULL_REASON_TEXT: Record<RevenueNullReason, string> = {
  no_orders: 'No orders',
  no_tickets: 'No tickets sold',
  no_revenue: 'No revenue',
  too_few_customers: 'Fewer than 20 identified customers',
  no_attributed_revenue: 'No revenue credited to a channel',
  range_too_short: 'Needs a range of at least 7 days',
}

/** Why a figure is not in the read model at all (`not_available`). */
export const NOT_AVAILABLE_TEXT: Record<RevenueNotAvailable, string> = {
  needs_browser_events: 'Needs browser events',
  needs_ad_platform: 'Needs a connected ad platform',
}

/** Why every `previous` is null (`meta.previous_unavailable`). */
export const PREVIOUS_UNAVAILABLE_TEXT: Record<PreviousUnavailable, string> = {
  currency_rate_unavailable: 'No exchange rate for the comparison',
  insufficient_history: 'Not a year of history yet',
  range_overlaps_comparison: 'Range longer than a year: no year-on-year comparison',
  not_compared: 'No comparison',
}

export const BASIS_NOUN: Record<RevenueBasis, string> = {
  gross: 'gross revenue',
  net: 'revenue net of refunds',
}

/** Each figure's definition, as the revenue definitions doc words it (§3). */
export const METRIC_DEFINITIONS: Record<SummaryMetricKey, string> = {
  revenue_gross: 'Sum of the gross amount of completed orders, on the tenant day each order completed.',
  revenue_net: 'Net before refunds of completed orders, less the refunds issued in the period; can be negative.',
  revenue_net_before_refunds: 'Sum of completed orders net of discounts, before refunds.',
  discounts: 'Gross less net before refunds, of completed orders.',
  orders: 'Completed orders that are not zero-value: free, postal and other orders whose gross is 0 are left out.',
  orders_zero_value: 'Completed orders whose gross is 0.',
  aov: 'Revenue under the page’s basis ÷ orders (zero-value orders excluded), rounded half-up.',
  tickets_sold: 'Tickets on completed orders’ competition lines, zero-value orders’ included.',
  average_ticket_price: 'Competition lines’ totals ÷ tickets sold, rounded half-up.',
  tickets_per_order: 'Tickets on counted orders with a competition line ÷ those orders.',
  refunds: 'Refunds issued in the period, whichever period their order completed in.',
  refund_count: 'Refunds issued in the period.',
  refund_rate: 'Refunds ÷ gross revenue.',
  revenue_per_visitor: 'Not available: visitors come from consented browser events, which this read model does not read.',
}

/** Text with its first letter capitalised, for a noun that opens a line. */
export function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** The page's tiles and charts name the basis's revenue so: "Gross revenue", "Net of refunds". */
export const REVENUE_LABEL: Record<RevenueBasis, string> = {
  gross: 'Gross revenue',
  net: 'Net of refunds',
}

export const BREAKDOWN_LABELS: Record<BreakdownDimension, string> = {
  competition: 'Competition',
  prize_category: 'Prize category',
  channel: 'Channel',
  campaign: 'Campaign',
  customer_type: 'Customer type',
  payment_method: 'Payment method',
}

/** Monday 0, as the timing route counts weekdays. */
export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
export const WEEKDAY_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const

export function hourLabel(hour: number): string {
  return `${String(hour % 24).padStart(2, '0')}:00`
}

/** A share or rate the API gives as a fraction. */
export function share(value: number | null): string {
  return value === null ? '—' : formatPercent(value)
}

/**
 * The comparison's own label — `prefix` and the range the API actually read (weekday-
 * aligned, so never just "the previous month"), "vs 27 Jun – 27 Jul 2026" — or why there is
 * none.
 */
export function comparisonLabel(frame: Pick<RevenueFrame, 'previousRange' | 'previousUnavailable'>, prefix = 'vs'): string {
  if (frame.previousUnavailable) return PREVIOUS_UNAVAILABLE_TEXT[frame.previousUnavailable]
  if (!frame.previousRange) return PREVIOUS_UNAVAILABLE_TEXT.not_compared
  return `${prefix} ${rangeLabel(frame.previousRange)}`
}

export function rangeLabel(range: { from: string; to: string }): string {
  if (range.from === range.to) return formatDateKey(range.from, true)
  const sameYear = range.from.slice(0, 4) === range.to.slice(0, 4)
  return `${formatDateKey(range.from, !sameYear)} – ${formatDateKey(range.to, true)}`
}

/** The 10-channel taxonomy's labels (`modules/analytics/traffic/channels.py` `CHANNEL_LABELS`), for a channel the API names by key alone. */
export const CHANNEL_LABELS: Readonly<Record<string, string>> = {
  direct: 'Direct',
  display: 'Display',
  paid_social: 'Paid social',
  paid_search: 'Paid search',
  paid_other: 'Other paid',
  email: 'Email',
  sms: 'SMS',
  affiliate: 'Affiliates',
  organic_social: 'Organic social',
  organic_search: 'Organic search',
  referral: 'Referral',
  other: 'Other',
}
